import {
    FamilyMember,
    FamilyMemberRelationship,
    FamilyMemberState,
    FamilyMemberStatus,
    FetchState,
} from "@/shared/types";
import {
    deleteFamilyMemberById,
    listFamilyMembers,
    updateFamily,
    addFamilyMember,
    updateFamilyMember
} from "@/shared/api/userService";
import moment from "moment";
import _ from "lodash";
import { defineStore } from "pinia";
import { useNotifierStore } from "@/shared/store/notifier";
import { useUserStore } from "@/shared/store/user";
import * as storeHelper from "@/shared/storeHelper";

const initialState: FamilyMemberState = {
    fetchState: FetchState.Uninitialized,
    family: {}
};

export const useFamilyStore = defineStore("family", {
    state: (): FamilyMemberState => (_.cloneDeep(initialState)),
    getters: {
        familyMembers: state => (state.fetchState === FetchState.Loaded) ?
            Object.keys(state.family).map(key => state.family[key])
            : null,
        userFamilyMember () {
            // this is a dummy family member used in the design. The birthday is not shown
            const userStore = useUserStore();
            const firstName = (userStore && userStore.user && userStore.user.firstName) || "";
            const lastName = (userStore && userStore.user && userStore.user.lastName) || "";
            const selfFamilyMember: FamilyMember = {
                id: null,
                firstName,
                middleInitial: "",
                lastName,
                tag: "Owner",
                dateOfBirth: null,
                ssn: "",
                debitCardHolder: true,
                relationship: FamilyMemberRelationship.None,
                isStudent: false,
                status: FamilyMemberStatus.None
            };
            return selfFamilyMember;
        },
        isFamilyStateLoaded: state => state.fetchState === FetchState.Loaded,
        noOfCardHolders () {
            return (this.familyMembers || []).filter(f => f.debitCardHolder).length + 1;
        },
        getFamilyMemberByExternalId () {
            return externalId => {
                return _.find(this.familyMembers, f => f.externalId === externalId);
            };
        }
    },
    actions: {
        setFetchState (newFetchState: FetchState) {
            this.fetchState = newFetchState;
        },
        setFamilyMembers (familyMembers: FamilyMember[]) {
            const temp = familyMembers.reduce((accumulator, current) => {
                current.dateOfBirth = moment(current.dateOfBirth).format("MM/DD/YYYY");
                accumulator[current.id] = current;
                return accumulator;
            }, {});
            this.family = temp;

        },
        initialize () {
            if (this.fetchState === FetchState.Loaded) {
                return Promise.resolve(this.familyMembers);
            }

            this.setFetchState(FetchState.Loading);
            return listFamilyMembers()
                .then(response => {
                    this.setFamilyMembers(response.data);
                    this.setFetchState(FetchState.Loaded);
                    return response.data;
                });
        },
        update (update: { newFamilyMembers: FamilyMember[] }) {
            const newMembers = update.newFamilyMembers.filter(f => !f.id);
            const updateFamilyMembers = update.newFamilyMembers.filter(f => f.id);
            const deleteMemberKeys = Object.keys(this.family)
                .map(stringKey => parseInt(stringKey))
                .filter(k => !updateFamilyMembers.some(updateMember => updateMember.id === k));

            for (const newMember of newMembers) {
                newMember.id = 0;
            }
            const newOrUpdated = [ ...newMembers, ...updateFamilyMembers ];
            return updateFamily({
                members: newOrUpdated
            }).then(response => this.setFamilyMembers(response.data.members))
                .then(() =>
                    Promise.all(_.map(deleteMemberKeys, key => deleteFamilyMemberById(key))));
        },
        addFamilyMember (familyMember: FamilyMember) {
            return addFamilyMember(familyMember)
                .then(response => {
                    const newMember = response.data;
                    const family = _.cloneDeep(this.family);

                    family[newMember.id] = newMember;
                    this.family = family;

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Dependent successfully added.");
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        },
        updateFamilyMember (params: { id: number, familyMember: FamilyMember }) {
            return updateFamilyMember(params.id, params.familyMember)
                .then(response => {
                    const updatedMember = response.data;
                    this.family[updatedMember.id] = updatedMember;

                    const notifierStore = useNotifierStore();
                    notifierStore.setSuccess("Dependent successfully updated.");
                })
                .catch(error => {
                    return storeHelper.handleError({ error, rethrow: true });
                });
        }
    }
});
